import { inject, Injectable } from '@angular/core'
import { _filterEmptyValues, _isNotNullish, _round } from '@naturalcycles/js-lib'
import {
  Goal,
  HARDWARE_THERMOMETERS,
  HardwareId,
  hwIdByProductKey,
  isThermometerKey,
  Price,
  Product,
  ProductKey,
  ProductType,
  ProductWithAddons,
  thermometerKeys,
  UserLocale,
} from '@naturalcycles/shared'
import { TranslateService } from '@ngx-translate/core'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { DiscountPipe } from '@src/app/shared/pipes/discount.pipe.js'
import { LocalizePipe } from '@src/app/shared/pipes/localize.pipe.js'
import { EnrichedAddon, EnrichedProduct } from '@src/app/shared/typings/interfaces/products.js'
import { Perk } from '../util/plansPage.util.js'
import { ouraRingDiscount } from '../util/product.util.js'
import { api } from './api.service.js'

@Injectable({ providedIn: 'root' })
export class ProductService {
  private translateService = inject(TranslateService)
  private discountPipe = inject(DiscountPipe)
  private localizePipe = inject(LocalizePipe)
  private store = inject(SignalStore)

  async getPlans(hwId: HardwareId | undefined, code?: string): Promise<Product[]> {
    const cart = this.store.$cart()
    const cartWearableVariant = cart.items.find(
      i => i.key === ProductKey.OURA_RING_DISCOUNT,
    )?.variant
    return await api.get(`product/plans`, {
      searchParams: _filterEmptyValues({ hwId, code, cartWearableVariant }),
    })
  }

  get(keys: ProductKey[] = [], types: ProductType[] = []): Product[] {
    return this.store
      .$product()
      .items.filter(product => !keys.length || keys.includes(product.key))
      .filter(product => !types.length || types.includes(product.type))
      .sort((a, b) => {
        if (types.indexOf(a.type) < types.indexOf(b.type)) return -1
        if (types.indexOf(a.type) > types.indexOf(b.type)) return 1
        if (keys.indexOf(a.key) < keys.indexOf(b.key)) return -1
        if (keys.indexOf(a.key) > keys.indexOf(b.key)) return 1
        return 0
      })
  }

  getDefaultThermometer(items: Product[]): ProductKey {
    const thermometerProduct = items.find(i => isThermometerKey(i.key))
    return thermometerProduct ? thermometerProduct.key : ProductKey.THERMOMETER
  }

  isThirdPartyDevice(hardware: ProductKey): boolean {
    return hardware !== ProductKey.B1 && !thermometerKeys.includes(hardware)
  }

  // TODO: move me to backend!
  getEnrichedAddOn(product: Product): EnrichedAddon | undefined {
    const addOn = (product as ProductWithAddons).addOns?.[0]
    if (!addOn) return
    const price = this.localizePipe.transform(addOn.price)

    // We don't use striked price for thermometers
    if (thermometerKeys.includes(addOn.key)) {
      return {
        ...addOn,
        title: isThermometerKey(addOn.key)
          ? this.translateService.instant(`cart-${addOn.key}`)
          : addOn.title,
        uiPrice: addOn.price.amount === 0 ? this.translateService.instant('tag-free') : price,
        selected: true,
      }
    }
    return {
      ...addOn,
      uiPrice:
        addOn.key === ProductKey.OURA_RING_DISCOUNT
          ? this.translateService.instant('oura-ring-price-text', { price })
          : price,
      uiOriginalPrice:
        addOn.price.cents !== addOn.originalPrice?.cents
          ? this.localizePipe.transform(addOn.originalPrice)
          : undefined,
      selected: true,
    }
  }

  private getUiStrikedPrice(product: Product): string | undefined {
    if (isThermometerKey(product.key)) {
      return this.localizePipe.transform(this.discountPipe.transform(product.price, 0))
    }
    if (product.marketPrice?.discount) {
      return this.localizePipe.transform(this.discountPipe.transform(product.marketPrice, 0))
    }

    return undefined
  }

  private getUiPrice(product: Product): string {
    if (isThermometerKey(product.key)) {
      return this.localizePipe.transform(this.discountPipe.transform(product.price, 100))
    }

    if (!product.marketPrice) return ''

    return this.localizePipe.transform(
      this.discountPipe.transform(product.marketPrice, product.marketPrice.discount),
    )
  }

  private getDescription(product: Product): string {
    if (product.type === ProductType.SUBSCRIPTION) {
      return ''
    }
    return isThermometerKey(product.key)
      ? this.translateService.instant('txt-device-info--default')
      : this.translateService.instant('txt-device-info--' + product.key)
  }

  private getTitle(product: Product): string {
    return product.type !== ProductType.SUBSCRIPTION
      ? this.translateService.instant('measuring-hw-name--' + product.key)
      : ''
  }

  private getEnrichedProductUi(product: Product): EnrichedProduct['ui'] {
    const translationKeySuffix = [
      ProductKey.OURA_RING_DISCOUNT,
      ProductKey.APPLE_WATCH,
      ProductKey.B1,
    ].includes(product.key)
      ? product.key
      : ProductKey.THERMOMETER
    return {
      title: this.getTitle(product),
      logo: this.isThirdPartyDevice(product.key)
        ? `/assets/img/icons/logo-black--${product.key}.svg`
        : undefined,
      subtitle: this.translateService.instant('measuring-hw-description--' + translationKeySuffix),
      description: this.getDescription(product),
      strikedPrice: this.getUiStrikedPrice(product),
      price: this.getUiPrice(product),
    }
  }

  public enrichProducts(products: Product[]): EnrichedProduct[] {
    return products.map(product => {
      return {
        ...product,
        addOn: this.getEnrichedAddOn(product),
        ui: this.getEnrichedProductUi(product),
      }
    })
  }

  public async getUIPlans(hwId: HardwareId): Promise<EnrichedProduct[]> {
    const { product, discountCode } = this.store.getState()
    try {
      const passDiscount = discountCode?.exists
      const plans = await this.getPlans(hwId, passDiscount ? discountCode?.code : undefined)
      return this.enrichProducts(plans)
    } catch {
      return this.enrichProducts(product.items.filter(i => i.type === ProductType.SUBSCRIPTION))
    }
  }

  /**
   * Calculates the saved percentage when subscribing yearly instead of monthly.
   * Uses `_round(n, 5)`
   * @returns A whole number representing the percentage saved.
   */
  public getSavingPercentage(
    yearly: Product,
    plans: Product[],
    hardwarePriceAmount?: number,
    discountedHardwarePriceAmount?: number,
  ): number {
    const monthly = plans.find(i => i.key === ProductKey.MONTH)
    if (!monthly || !yearly.duration) return 0

    const monthlyFor12months = monthly.price.amount * yearly.duration + (hardwarePriceAmount || 0)
    const yearlyFor12months = yearly.price.amount + (discountedHardwarePriceAmount || 0)

    if (monthlyFor12months === 0) return 0

    const savings = (1 - yearlyFor12months / monthlyFor12months) * 100
    return _round(savings, 5)
  }

  /**
   * Calculates the amount saved between monthly and yearly subscriptions.
   * The savings will NOT include hardware price.
   */
  calculateSavingsAmount(yearly: Product, monthly: Product, locale: UserLocale): string {
    const monthlyFor12months = monthly.price.amount * yearly.duration!
    const savings = monthlyFor12months - yearly.price.amount
    const roundedSavings = _round(savings, 1)
    return new Price(`${roundedSavings}[${locale.pricingCurrency}]`).toLocaleString(
      locale.supportedLanguage,
    )
  }

  /**
   * Calculates the amount user will pay yearly if they subscribe to monthly plan for a year.
   */
  public calculateMonthlySubscriptionToYearAmount(monthly: Product, locale: UserLocale): string {
    const monthlyFor12months = monthly.price.amount * 12
    const roundedSavings = _round(monthlyFor12months, 1)
    return new Price(`${roundedSavings}[${locale.pricingCurrency}]`).toLocaleString(
      locale.supportedLanguage,
    )
  }

  /**
   * Determines how many months are free when subscribing to a yearly plan.
   */
  public calculateFreeMonthsGiven(
    yearly: Product,
    monthly: Product,
    selectedHwId: HardwareId,
  ): number {
    const monthlyPrice = monthly.price.amount
    const yearlyPrice = yearly.price.amount

    const thermometer = this.store
      .$product()
      .items.find(product => product.hwId === selectedHwId && isThermometerKey(product.key))

    const thermometerPrice = thermometer?.price.amount || 0
    const totalCost = monthlyPrice * 12 + thermometerPrice
    const savings = totalCost - yearlyPrice
    const savingsPercentage = savings / totalCost

    return Math.round(savingsPercentage * 12)
  }

  private getSavingsTag(
    selectedHwId: HardwareId,
    plan: EnrichedProduct | undefined,
  ): string | undefined {
    const monthlyPlan = this.store.$product().items.find(p => p.key === ProductKey.MONTH)
    const yearlyPlan = this.store.$product().items.find(p => p.key === ProductKey.YEAR)
    let hardwarePrice: number | undefined

    if (HARDWARE_THERMOMETERS.includes(selectedHwId) || selectedHwId === HardwareId.B1) {
      const thermometerProduct = this.get(thermometerKeys).find(p => p.hwId === selectedHwId)
      if (thermometerProduct) {
        hardwarePrice = thermometerProduct.price.amount
      }
    }

    let discountedHardwarePrice: number | undefined
    if (selectedHwId === HardwareId.B1) {
      discountedHardwarePrice = plan?.addOn?.price.amount
      hardwarePrice = plan?.addOn?.originalPrice?.amount
    }

    let savingsTag: string | undefined
    if (yearlyPlan && monthlyPlan) {
      savingsTag = `${this.getSavingPercentage(yearlyPlan, [monthlyPlan], hardwarePrice, discountedHardwarePrice)}%`
    }

    return savingsTag
  }

  private getThermometerYearlyPerks(
    selectedHwId: HardwareId,
    locale: UserLocale,
    savingsTag?: string,
    goal?: Goal,
  ): Perk[] {
    return [
      { text: this.translateService.instant('txt-plan-save-amount', { savingsTag }) },
      {
        text: this.translateService.instant(
          locale.country === 'US' && goal === Goal.PREVENT
            ? 'perk-covered-insurance'
            : 'txt-plan-popular-choice',
        ),
      },
      {
        text: this.translateService.instant('txt-plan-free-thermo'),
        imageUrl: `https://www.datocms-assets.com/21281/${selectedHwId === HardwareId.T3_THERMOMETER ? '1745316698-t3_tiny' : '1744117904-t2_tiny'}.png`,
      },
    ]
  }

  private getAllYearlyPlanPerks(
    selectedHwId: HardwareId,
    plan: EnrichedProduct,
    params: {
      locale: UserLocale
      goal: Goal | undefined
      ownsHardware: boolean | undefined
      isTest368: boolean | undefined
    },
  ): Partial<Record<HardwareId, Perk[]>> {
    // AB368
    const ouraRingDiscountPercentage = params.isTest368 ? 'up to $50' : ouraRingDiscount
    const savingsTag = this.getSavingsTag(selectedHwId, plan)

    const lang = this.store.$lang()
    const thermometerYearPlanPerks = this.getThermometerYearlyPerks(
      selectedHwId,
      params.locale,
      savingsTag,
      params.goal,
    )

    return {
      [HardwareId.ORAL_THERMOMETER]: thermometerYearPlanPerks,
      [HardwareId.UEBE_THERMOMETER]: thermometerYearPlanPerks,
      [HardwareId.T3_THERMOMETER]: thermometerYearPlanPerks,

      [HardwareId.APPLE_WATCH]: [
        { text: this.translateService.instant('txt-plan-save-amount', { savingsTag }) },
        {
          text: this.translateService.instant(
            params.locale.country === 'US' && params.goal === Goal.PREVENT
              ? 'perk-covered-insurance'
              : 'txt-plan-popular-choice',
          ),
        },
        { text: this.translateService.instant('txt-plan-less-stress') },
      ],
      [HardwareId.OURA]: [
        { text: this.translateService.instant('txt-plan-save-amount', { savingsTag }) },
        {
          text: this.translateService.instant(
            params.locale.country === 'US' && params.goal === Goal.PREVENT
              ? 'perk-covered-insurance'
              : 'txt-plan-popular-choice',
          ),
        },
        ...(params.ownsHardware
          ? [{ text: this.translateService.instant('txt-plan-less-stress') }]
          : [
              {
                text: this.translateService.instant('txt-plan-oura-discount', {
                  amount: ouraRingDiscountPercentage,
                }),
              },
            ]),
      ],
      [HardwareId.B1]: [
        {
          text: this.translateService.instant('txt-plan-save-amount', { savingsTag }),
        },
        {
          text: this.translateService.instant('perk-covered-insurance'),
        },
        {
          text: `+ <s>${plan?.addOn?.originalPrice?.toLocaleString(lang)}</s> <span><b>${plan?.addOn?.price.toLocaleString(lang)}</b></span>  ${this.translateService.instant('product--B1--title')}`,
          imageUrl: `https://www.datocms-assets.com/21281/1746697316-tiny-b1.svg`,
        },
      ],
    }
  }

  private getAllMonthlyPlanPerks(
    isTest368: boolean | undefined,
  ): Partial<Record<HardwareId, Perk[]>> {
    const ouraRingDiscountPercentage = isTest368 ? 'up to $50' : ouraRingDiscount

    return {
      [HardwareId.OURA]: [
        {
          text: this.translateService.instant('txt-plan-oura-discount', {
            amount: ouraRingDiscountPercentage,
          }),
        },
      ],
    }
  }

  public getPerks(
    selectedHwId: HardwareId,
    params: {
      locale: UserLocale
      goal?: Goal
      ownsHardware?: boolean
      isTest368?: boolean
    },
    plan: EnrichedProduct,
  ): Perk[] {
    const { locale, goal, ownsHardware, isTest368 } = params
    const allHardwarePerksYearly = this.getAllYearlyPlanPerks(selectedHwId, plan, {
      locale,
      goal,
      ownsHardware,
      isTest368,
    })
    const allHardwarePerksMonthly = this.getAllMonthlyPlanPerks(isTest368)

    if (plan?.key === ProductKey.YEAR) {
      return allHardwarePerksYearly[selectedHwId] || []
    }

    if (plan?.key === ProductKey.MONTH && !ownsHardware) {
      return allHardwarePerksMonthly[selectedHwId] || []
    }

    return []
  }

  private getAddonMonthlyPlan(plan: EnrichedProduct, selectedHwId: HardwareId): string | undefined {
    const thermometerHwIds = thermometerKeys.map(k => hwIdByProductKey[k]).filter(_isNotNullish)
    const hasThermometer = thermometerHwIds.includes(selectedHwId)
    const lang = this.store.$lang()

    if (plan.addOn?.optional && hasThermometer) {
      const translatedText = this.translateService.instant('txt-plan-extra-thermo')
      return `+ <span>${plan.addOn?.price.toLocaleString(lang)}</span> ${translatedText}`
    }

    if (plan.addOn?.key === ProductKey.B1) {
      return `+ <span>${plan.addOn?.price.toLocaleString(lang)}</span> ${this.translateService.instant('product--B1--title')}`
    }
  }

  public getAddOnTextForPlan(selectedHwId: HardwareId, plan: EnrichedProduct): string | undefined {
    if (plan.key === ProductKey.MONTH) {
      return this.getAddonMonthlyPlan(plan, selectedHwId)
    }
  }

  public getTranslatedPlanTitle(plan: EnrichedProduct): string {
    return this.translateService.instant(`product-title-app--${plan.key}`)
  }
}
